import { Component, Vue, Watch } from 'vue-property-decorator'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { BBadge, BButton } from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import TaskItem from '../TaskItem/TaskItem.vue'
import { emitter, Spinner } from '@kwixeo/interface-vue'

@Component({
  computed: {
    ...mapGetters('user', ['teamUserConfigs', 'can', 'teamUser']),
    ...mapGetters('subscriptions', ['hasModule'])
  },
  methods: {
    ...mapActions('dynamicModals', ['openModal'])
  },
  components: {
    FontAwesomeIcon,
    BBadge, BButton,
    TaskItem,
    Spinner
  }
})
export default class TaskHeader extends Vue {
  teamUserConfigs!: KwixeoFramework.ConfigAttributes
  hasModule!: (key: string) => boolean
  can!: (key: string, level: number) => boolean
  teamUser!: KwixeoFramework.TeamUser

  private tasks: KwixeoTasks.Tasks = []
  private total = 0
  private maxTasks = 5
  private loading = false

  async created (): Promise<void> {
    if (this.taskIsActive) {
      await this.getTasks()
    }
  }

  get taskIsActive (): boolean {
    return this.hasModule('task') && this.can('task', 1)
  }

  @Watch('taskIsActive')
  private async watchTaskIsActive (): Promise<void> {
    if (this.taskIsActive) {
      await this.getTasks()
    }
  }

  mounted (): void {
    emitter.on('taskCreated', this.taskCreated)
    emitter.on('taskUpdated', this.taskUpdated)
    emitter.on('taskDeleted', this.taskDeleted)
  }

  destroyed (): void {
    emitter.off('taskCreated', this.taskCreated)
    emitter.off('taskUpdated', this.taskUpdated)
    emitter.off('taskDeleted', this.taskDeleted)
  }

  get deadline (): string {
    return moment().add(this.teamUserConfigs.task_header_limit, 'days').format('YYYY-MM-DD')
  }

  get moreTask (): number {
    return this.total - this.tasks.length
  }

  private async getTasks (): Promise<void> {
    this.loading = true
    await this.$kwTasks.axios.get('/task', {
      params: {
        per_page: this.maxTasks,
        filter: {
          deadline_until: this.deadline,
          status: [1, 2, 3],
          with_team_users: this.teamUser.id
        },
        with_file: true
      }
    })
      .then(response => {
        this.tasks = response.data.data
        this.total = response.data.meta.total
      })
      .catch(error => {
        throw new Error(error)
      })
    this.loading = false
  }

  // Event when a task is created
  private taskCreated (task: KwixeoTasks.Task): void {
    const index = this.tasks.findIndex(val => val.id === task.id)
    if (index >= 0) {
      this.taskUpdated({ old: null, new: task })
      return
    }

    if (task.status.value === 4 || moment(task.datetime).isAfter(this.deadline) || !task.team_users.find(val => val.id === this.teamUser.id)) {
      return
    }

    if (this.tasks.length < this.maxTasks) {
      this.tasks.push(task)
    }
    this.updateTotal(+1)
  }

  // Event when a task is updated
  private taskUpdated (event: KwixeoTasks.TaskEmitterEvent): void {
    const task = event.new
    const index = this.tasks.findIndex(val => val.id === task.id)

    if (index >= 0) {
      if (task.status.value !== 4 && moment(task.datetime).isSameOrBefore(this.deadline) && task.team_users.find(val => val.id === this.teamUser.id)) {
        Vue.set(this.tasks, index, task)
      } else {
        this.updateTotal(-1)
        Vue.delete(this.tasks, index)
      }
    } else {
      this.taskCreated(task)
    }
  }

  // Event when a task is deleted
  private async taskDeleted (id: number): Promise<void> {
    const index = this.tasks.findIndex(val => val.id === id)
    if (index >= 0) {
      Vue.delete(this.tasks, index)
      this.updateTotal(-1)
    }
  }

  // Update the total task number
  private updateTotal (mvt: number): void {
    let total = this.total + mvt
    if (total < 0) {
      total = 0
    }
    this.total = total
  }
}
